import React, { useEffect, useState, useMemo } from 'react';
import { Clock, AlertTriangle } from 'lucide-react';

interface QuestionTimerProps {
  timeLimit: number; // Time limit in seconds
  onTimeUp: () => void;
  show: boolean;
  position?: 'top-right' | 'top-center' | 'bottom-right';
  questionId?: string; // To reset timer on question change
  isPaused?: boolean;
}

const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const QuestionTimer: React.FC<QuestionTimerProps> = ({
  timeLimit,
  onTimeUp,
  show,
  position = 'top-right',
  questionId,
  isPaused = false
}) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [showWarning, setShowWarning] = useState(false);
  const [showCritical, setShowCritical] = useState(false);

  // Reset timer when question changes
  useEffect(() => {
    setTimeLeft(timeLimit);
    setShowWarning(false);
    setShowCritical(false);
  }, [questionId, timeLimit]);

  // Timer countdown logic
  useEffect(() => {
    if (!show || isPaused || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;

        // Warning states
        if (newTime === 30 && !showWarning) {
          setShowWarning(true);
          // Show notification
          const notification = document.createElement('div');
          notification.className = 'question-timer-notification warning';
          notification.textContent = '30 saniye kaldı!';
          document.body.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);
        }

        if (newTime === 10 && !showCritical) {
          setShowCritical(true);
          // Show critical notification
          const notification = document.createElement('div');
          notification.className = 'question-timer-notification critical';
          notification.textContent = '10 saniye kaldı!';
          document.body.appendChild(notification);
          setTimeout(() => notification.remove(), 3000);
        }

        if (newTime <= 0) {
          onTimeUp();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [show, isPaused, timeLeft, onTimeUp, showWarning, showCritical]);

  if (!show) return null;

  // Position classes
  const positionClasses = {
    'top-right': 'top-20 right-4',
    'top-center': 'top-20 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-20 right-4'
  };

  // Color classes based on time
  const colorClasses = useMemo(() => {
    if (showCritical) return 'bg-red-500 text-white animate-pulse';
    if (showWarning) return 'bg-orange-500 text-white';
    return 'bg-gray-800 text-white';
  }, [showCritical, showWarning]);

  return (
    <>
      <div
        className={`
          fixed z-50 px-4 py-2 rounded-lg shadow-lg
          flex items-center gap-2 font-medium
          transition-all duration-300
          ${positionClasses[position]}
          ${colorClasses}
        `}
      >
        {showCritical ? (
          <AlertTriangle size={18} className="animate-bounce" />
        ) : (
          <Clock size={18} />
        )}
        <span className="text-lg tabular-nums">{formatTime(timeLeft)}</span>
      </div>

      <style>{`
        .question-timer-notification {
          position: fixed;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          padding: 16px 32px;
          border-radius: 12px;
          font-size: 24px;
          font-weight: bold;
          z-index: 9999;
          animation: slideIn 0.3s ease-out;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
        }

        .question-timer-notification.warning {
          background-color: #f97316;
          color: white;
        }

        .question-timer-notification.critical {
          background-color: #ef4444;
          color: white;
          animation: slideIn 0.3s ease-out, shake 0.5s ease-in-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translate(-50%, -40%);
          }
          to {
            opacity: 1;
            transform: translate(-50%, -50%);
          }
        }

        @keyframes shake {
          0%, 100% { transform: translate(-50%, -50%) rotate(0deg); }
          25% { transform: translate(-50%, -50%) rotate(-5deg); }
          75% { transform: translate(-50%, -50%) rotate(5deg); }
        }
      `}</style>
    </>
  );
};

// Mobile version with compact design
export const MobileQuestionTimer: React.FC<QuestionTimerProps> = (props) => {
  const { timeLeft, showWarning, showCritical } = useQuestionTimer(props);

  if (!props.show) return null;

  const colorClasses = showCritical 
    ? 'bg-red-500' 
    : showWarning 
    ? 'bg-orange-500' 
    : 'bg-gray-700';

  return (
    <div
      className={`
        flex items-center gap-1 h-full text-xs font-medium
        ${showCritical ? 'text-red-600' : showWarning ? 'text-orange-600' : 'text-gray-700'}
        ${showCritical ? 'animate-pulse' : ''}
      `}
    >
      <Clock className="w-3 h-3" />
      <span className="font-mono">{formatTime(timeLeft)}</span>
    </div>
  );
};

// Custom hook for timer logic
const useQuestionTimer = ({
  timeLimit,
  onTimeUp,
  questionId,
  isPaused = false
}: Omit<QuestionTimerProps, 'show' | 'position'>) => {
  const [timeLeft, setTimeLeft] = useState(timeLimit);
  const [showWarning, setShowWarning] = useState(false);
  const [showCritical, setShowCritical] = useState(false);

  useEffect(() => {
    setTimeLeft(timeLimit);
    setShowWarning(false);
    setShowCritical(false);
  }, [questionId, timeLimit]);

  useEffect(() => {
    if (isPaused || timeLeft <= 0) return;

    const interval = setInterval(() => {
      setTimeLeft(prev => {
        const newTime = prev - 1;

        if (newTime === 30) setShowWarning(true);
        if (newTime === 10) setShowCritical(true);
        if (newTime <= 0) {
          onTimeUp();
          return 0;
        }

        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused, timeLeft, onTimeUp]);

  return { timeLeft, showWarning, showCritical };
};

export default QuestionTimer;